import { StreamCoordinatesGet, StreamCoordinates } from "./interface";
import { Layout } from "./Layout";
import { assert, toDiv } from "../../utils";

export class SidebarLayout implements Layout {
  constructor(private readonly config: Config) {}

  streamCoordinatesGet(
    streamCoordinates: StreamCoordinatesGet
  ): StreamCoordinates {
    const { streamMargin } = this.config;
    const { x, y, width, height } =
      this.initialStreamCoordinatesGet(streamCoordinates);

    const aspect = width / height;
    const width1 = toDiv(width - 2 * streamMargin, 8);
    const height1 = toDiv(width1 / aspect, 8);

    const padWidth = Math.floor((aspect * height1) / 2) * 2;
    const padX = Math.floor((width - padWidth) / 4) * 2;

    return {
      x: x + padX,
      y: y + (height - height1) / 2,
      width: padWidth,
      height: height1
    };
  }

  private initialStreamCoordinatesGet(
    streamCoordinatesGet: StreamCoordinatesGet
  ): StreamCoordinates {
    const { width, height } = this.config;
    const { streamIndex, streamsCount } = streamCoordinatesGet;

    switch (streamsCount) {
      case 1: {
        assert(
          [0].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} data`
        );

        return {
          width,
          height,
          x: 0,
          y: 0
        };
      }
      case 2: {
        assert(
          [0, 1].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} data`
        );

        if (streamIndex === 0) {
          return {
            width: width * (3 / 4),
            height: height * (3 / 4),
            x: 0,
            y: (height - height * (3 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: width * (3 / 4),
          y: (height - height * (1 / 4)) / 2
        };
      }
      case 3: {
        assert(
          [0, 1, 2].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} data`
        );

        if (streamIndex === 0) {
          return {
            width: width * (3 / 4),
            height: height * (3 / 4),
            x: 0,
            y: (height - height * (3 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: width * (3 / 4),
          y:
            (height - height * (2 / 4)) / 2 +
            (streamIndex - 1) * (height * (1 / 4))
        };
      }
      case 4: {
        assert(
          [0, 1, 2, 3].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        if (streamIndex === 0) {
          return {
            width: width * (3 / 4),
            height: height * (3 / 4),
            x: 0,
            y: (height - height * (3 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: width * (3 / 4),
          y:
            (height - height * (3 / 4)) / 2 +
            (streamIndex - 1) * (height * (1 / 4))
        };
      }
      case 5: {
        assert(
          [0, 1, 2, 3, 4].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        if (streamIndex === 0) {
          return {
            width: width * (3 / 4),
            height: height * (3 / 4),
            x: 0,
            y: (height - height * (3 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: width * (3 / 4),
          y: ((streamIndex - 1) % 4) * (height * (1 / 4))
        };
      }
      case 6: {
        assert(
          [0, 1, 2, 3, 4, 5].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        if (streamIndex === 0) {
          return {
            width: width * (2 / 4),
            height: height * (2 / 4),
            x: 0,
            y: (height - height * (2 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: [1, 2, 3, 4].includes(streamIndex)
            ? width * (2 / 4)
            : width * (3 / 4),
          y: ((streamIndex - 1) % 4) * (height * (1 / 4))
        };
      }
      case 7: {
        assert(
          [0, 1, 2, 3, 4, 5, 6].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        if (streamIndex === 0) {
          return {
            width: width * (2 / 4),
            height: height * (2 / 4),
            x: 0,
            y: (height - height * (2 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: [1, 2, 3, 4].includes(streamIndex)
            ? width * (2 / 4)
            : width * (3 / 4),
          y: ((streamIndex - 1) % 4) * (height * (1 / 4))
        };
      }
      case 8: {
        assert(
          [0, 1, 2, 3, 4, 5, 6, 7].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        if (streamIndex === 0) {
          return {
            width: width * (2 / 4),
            height: height * (2 / 4),
            x: 0,
            y: (height - height * (2 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: [1, 2, 3, 4].includes(streamIndex)
            ? width * (2 / 4)
            : width * (3 / 4),
          y: ((streamIndex - 1) % 4) * (height * (1 / 4))
        };
      }
      case 9: {
        assert(
          [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        if (streamIndex === 0) {
          return {
            width: width * (2 / 4),
            height: height * (2 / 4),
            x: 0,
            y: (height - height * (2 / 4)) / 2
          };
        }

        return {
          width: width * (1 / 4),
          height: height * (1 / 4),
          x: [1, 2, 3, 4].includes(streamIndex)
            ? width * (2 / 4)
            : width * (3 / 4),
          y: ((streamIndex - 1) % 4) * (height * (1 / 4))
        };
      }
      default:
        assert(false, `Invalid streamsCount ${streamsCount}`);
    }
  }
}

interface Config {
  width: number;
  height: number;
  streamMargin: number;
}
